import React from 'react';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { State } from 'store/state';
import { Route } from 'config/routes';
import ChevronIcon from 'assets/svg/arrow-chevron-left.svg';
import * as Styled from './styled';
import { PoiId, MarketplaceType } from 'utils/entities';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { CenteredCarouselControls } from 'ds/components/carousels/styled';
import Carousel from 'ds/components/carousels/carousel';
import { ItemWrapper } from 'ds/components/carousels/carousel/styled';
import { Link } from 'components/link';
import UniversalCard from 'components/cards/listing/UniversalCard';
import {
  PoiGetter,
  makeMapStateToPropsFactory,
} from 'components/cards/listing/makeMapStateToPropsFactory';
import { UniversalCardProps } from 'components/cards/listing/UniversalCard/UniversalCard';
import { Section } from 'components/navigation/subheader/SectionsNavigation';
import { allAgentAndOfficeListingsSelector } from 'store/state/domainData/selectors/agentWithBulletinsByIds';
import { makeByIdsDictionarySelector } from 'store/state/domainData/selectors/common';
import { combinedMarketplaceOfficeListingsSelector } from 'store/state/domainData/selectors/officeListings';
import { createSelector } from 'reselect';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';

interface MadadInfoCarouselProps {
  moreLinkRoute: Route;
  moreLinkParamsId: string;
  carouselKey: symbol;
  marketplace: MarketplaceType;
  listingsIds: string[];
  from: string;
  onSwipeIndexChange?: (prev: number, current: number) => void;
  onClickIndexChange?: (direction: 'right' | 'left') => void;
  type: 'agent' | 'office';
  trackingEventSource?: string;
}



const allAgentAndOfficeListingsDictionarySelector = makeByIdsDictionarySelector(allAgentAndOfficeListingsSelector);
const officeListingsByIdsSelector = makeByIdsDictionarySelector(combinedMarketplaceOfficeListingsSelector);

const agentAndOfficeListingsSelector = createSelector([
  allAgentAndOfficeListingsDictionarySelector,
  officeListingsByIdsSelector,
], (agentsMap, officeMap) => ({
  ...agentsMap,
  ...officeMap,
}));

const Card: React.FC<UniversalCardProps> = props => <UniversalCard {...props} hideLogoPreview isMinHeight />;

const poiByIdsSelector = (state: State, poiId: PoiId) => agentAndOfficeListingsSelector(state)[poiId];
const makeMapStateToProps = makeMapStateToPropsFactory(poiByIdsSelector as unknown as PoiGetter);
const ConnectedUniversalCard = connect(makeMapStateToProps)(Card);


const MadadInfoCarousel: React.FC<MadadInfoCarouselProps> = (props) => {
  const {
    moreLinkRoute,
    moreLinkParamsId,
    carouselKey,
    marketplace,
    listingsIds,
    from,
    onSwipeIndexChange = noop,
    onClickIndexChange = noop,
    type,
    trackingEventSource,
  } = props;

  const { t } = useLocale();
  const isAgentType = type === 'agent';
  const scrollBoxIndexRef = React.useRef(0);
  const routeName = marketplace === MarketplaceType.Commercial ? Route.UnitPageCommercial : Route.UnitPage;
  const hasArrows = listingsIds.length > 2;
  const isMobile = useScreenBreakpoint() < 3;
  const source = isAgentType ? 'leading_agent_carousel' : trackingEventSource;

  const onSwipe = (idx: number) => {
    if (isAgentType) {
      onSwipeIndexChange(scrollBoxIndexRef.current, idx);
    }
    scrollBoxIndexRef.current = idx;
  };

  const listings = listingsIds.map((id) => (
    <ItemWrapper key={id}>
      <Link
        routeName={routeName}
        routeParams={{ id, tracking_event_source: source }}
      >
        <ConnectedUniversalCard id={id} />
      </Link>
    </ItemWrapper>
  ));

  return (
    <Styled.Root>
      <Styled.HeaderWrapper isMobile={isMobile}>
        <Styled.Title weight="bold">
          {t('madad.agent.more.title.copy', { from })}
        </Styled.Title>
        <Styled.DeveloperLink
          routeName={moreLinkRoute}
          routeParams={{ id: moreLinkParamsId, scrollTo: Section.LISTINGS }}
        >
          <Styled.DeveloperLinkText weight="medium">
            {t('madad.info.carousel.more.copy')}
            <ChevronIcon />
          </Styled.DeveloperLinkText>
        </Styled.DeveloperLink>
      </Styled.HeaderWrapper>

      <ShowAt to={2}>
        <ScrollBox
          cacheKey={carouselKey}
          autoWidth
          onSlideIndexChange={onSwipe}
        >
          {listings}
        </ScrollBox>
      </ShowAt>
      <ShowAt from={3}>
        <CenteredCarouselControls>
          <Carousel
            hasArrows={hasArrows}
            onScrollLeft={() => onClickIndexChange('left')}
            onScrollRight={() => onClickIndexChange('right')}
          >
            {listings}
          </Carousel>
        </CenteredCarouselControls>
      </ShowAt>
    </Styled.Root>
  );
};


const MadadInfoCarouselWithEvents = withEvents<MadadInfoCarouselProps>((sendEvent, props) => ({
  onSwipeIndexChange(prevIndex: number, slideIndex: number) {
    const isAgentType = props.type === 'agent';
    if (isAgentType) {
      const direction = (prevIndex || 0) < slideIndex ? 'right' : 'left';
      sendEvent('agent_carousel_scroll', 'agent', {
        agent_details: {},
        office_details: {},
        event: {
          carousel_type: 'leading_agent_listings',
          direction,
          action: 'swipe',
        },
      });
    }
  },
  onClickIndexChange(direction: 'right' | 'left') {
    const isAgentType = props.type === 'agent';
    if (isAgentType) {
      sendEvent('agent_carousel_scroll', 'agent', {
        agent_details: {},
        office_details: {},
        event: {
          carousel_type: 'leading_agent_listings',
          direction,
          action: 'click',
        },
      });
    }
  },
}))(MadadInfoCarousel);

export default MadadInfoCarouselWithEvents;
